import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import {
  getSettings,
  updateSetting,
  restoreSettingDefault,
  restoreAllSettingsDefaults,
} from "../../../services/SettingsService";
import { useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

const Settings = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canUpdate = can(PERMISSIONS.SETTING_UPDATE);
  const canRestoreDefaults = can(PERMISSIONS.SETTING_RESTORE_DEFAULTS);

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editedValues, setEditedValues] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showRestoreAllModal, setShowRestoreAllModal] = useState(false);
  const [restoringAll, setRestoringAll] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getSettings();
      const rows = res.data?.data || [];
      setSettings(rows);
      const initial = {};
      rows.forEach((row) => {
        initial[row.key] = row.value;
      });
      setEditedValues(initial);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.settings.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const handleChange = (key, value) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (row) => {
    setSavingKey(row.key);
    try {
      const res = await updateSetting(row.key, editedValues[row.key]);
      const updated = res.data?.data;
      setSettings((prev) => prev.map((s) => (s.key === row.key ? updated : s)));
      setFeedback({ type: "success", message: t("pages.settings.updateSuccess") });
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.settings.updateError") });
    } finally {
      setSavingKey(null);
    }
  };

  const handleRestoreDefault = async (row) => {
    setSavingKey(row.key);
    try {
      const res = await restoreSettingDefault(row.key);
      const updated = res.data?.data;
      setSettings((prev) => prev.map((s) => (s.key === row.key ? updated : s)));
      setEditedValues((prev) => ({ ...prev, [row.key]: updated.value }));
      setFeedback({ type: "success", message: t("pages.settings.restoreDefaultSuccess") });
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.settings.restoreDefaultError") });
    } finally {
      setSavingKey(null);
    }
  };

  const handleRestoreAll = async () => {
    setRestoringAll(true);
    try {
      await restoreAllSettingsDefaults();
      setFeedback({ type: "success", message: t("pages.settings.restoreAllSuccess") });
      setShowRestoreAllModal(false);
      fetchSettings();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.settings.restoreAllError") });
    } finally {
      setRestoringAll(false);
    }
  };

  const renderInput = (row) => {
    const currentValue = editedValues[row.key];
    if (typeof row.default_value === "boolean") {
      return (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={!!currentValue}
            onChange={(e) => handleChange(row.key, e.target.checked)}
          />
        </div>
      );
    }
    if (typeof row.default_value === "number") {
      return (
        <input
          type="number"
          className="form-control"
          value={currentValue ?? 0}
          onChange={(e) => handleChange(row.key, Number(e.target.value))}
          style={{ maxWidth: "160px" }}
        />
      );
    }
    return (
      <input
        type="text"
        className="form-control"
        value={currentValue ?? ""}
        onChange={(e) => handleChange(row.key, e.target.value)}
      />
    );
  };

  const hasChanged = (row) => JSON.stringify(editedValues[row.key]) !== JSON.stringify(row.value);
  const isDefault = (row) => JSON.stringify(row.value) === JSON.stringify(row.default_value);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.settings")}</li>
              </ol>
            </nav>
          </div>
          {canRestoreDefaults && (
            <div className="mb-2">
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowRestoreAllModal(true)}>
                <i className="ti ti-refresh-alert me-1"></i>{t("pages.settings.resetAll")}
              </button>
            </div>
          )}
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header">
            <h4 className="mb-0">{t("pages.settings.title")}</h4>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t("common.crud.loading")}</span>
                </div>
              </div>
            ) : loadError ? (
              <div className="alert alert-warning m-3">{loadError}</div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: "30%" }}>{t("common.ui.parametre")}</th>
                      <th style={{ width: "25%" }}>{t("pages.settings.valueColumn")}</th>
                      <th>{t("common.crud.statusColumn")}</th>
                      <th>{t("common.crud.actionColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <p className="mb-0 fw-medium">{row.key}</p>
                          <small className="text-muted">{row.description}</small>
                        </td>
                        <td>{renderInput(row)}</td>
                        <td>
                          {isDefault(row) ? (
                            <span className="badge badge-soft-secondary">{t("pages.settings.defaultValueBadge")}</span>
                          ) : (
                            <span className="badge badge-soft-info">{t("pages.settings.customBadge")}</span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={!hasChanged(row) || savingKey === row.key}
                                onClick={() => handleSave(row)}
                              >
                                {savingKey === row.key ? "..." : t("common.crud.save")}
                              </button>
                            )}
                            {canRestoreDefaults && !isDefault(row) && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={savingKey === row.key}
                                onClick={() => handleRestoreDefault(row)}
                              >
                                {t("pages.settings.reset")}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={showRestoreAllModal} onHide={() => (restoringAll ? null : setShowRestoreAllModal(false))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-refresh-alert"></i>
          </span>
          <h4>{t("pages.settings.confirmResetAllTitle")}</h4>
          <p>{t("pages.settings.confirmResetAllBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setShowRestoreAllModal(false)} disabled={restoringAll}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={handleRestoreAll} disabled={restoringAll}>
              {restoringAll ? t("pages.settings.resetting") : t("pages.settings.confirmResetAll")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Settings;
